// SAP Business Partner Integration Service
import { API_CONFIG, DEBUG_CONFIG } from '../config';

/**
 * Creates a Business Partner record in SAP
 * @param {Object} customerData - Customer form data
 * @param {string} sfdcAccountId - Salesforce Account ID to link
 * @returns {Promise<Object>} SAP response with Business Partner ID
 */
export const createSapBusinessPartner = async (customerData, sfdcAccountId) => {
  if (DEBUG_CONFIG.LOG_API_CALLS) {
    console.log('Creating SAP Business Partner with data:', customerData);
  }

  // Temporarily disabled by default: the SAP demo endpoint can respond with 401 + WWW-Authenticate,
  // which triggers the browser's native login prompt when routed through the dev proxy.
  // Re-enable only when proper auth handling is in place.
  const sapEnabled = (import.meta.env.VITE_ENABLE_SAP_API || 'false') === 'true';
  if (!sapEnabled) {
    return {
      success: false,
      message: 'SAP integration is temporarily disabled in this build',
      businessPartnerId: null
    };
  }

  try {
    // Build the SAP payload
    const payload = buildSapBusinessPartnerPayload(customerData, sfdcAccountId);
    
    if (DEBUG_CONFIG.LOG_API_CALLS) {
      console.log('SAP BP Payload:', JSON.stringify(payload, null, 2));
    }

    // Use proxy URL for development to avoid CORS issues
    // In production, SAP calls should be handled by backend API
    const sapUrl = import.meta.env.DEV
      ? '/api/sap/A_BusinessPartner'
      : null;

    if (!sapUrl) {
      return {
        success: false,
        message: 'SAP integration will be handled by backend API',
        businessPartnerId: null
      };
    }

    const response = await fetch(sapUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-csrf-token': API_CONFIG.SAP_CSRF_TOKEN,
        'Accept': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`SAP BP API Error: ${response.status} ${response.statusText} - ${errorText}`);
    }

    // SAP returns XML by default, but we're requesting JSON
    const result = await response.json();
    
    if (DEBUG_CONFIG.LOG_API_CALLS) {
      console.log('SAP BP API Response:', result);
    }

    // Extract Business Partner ID from the response
    let businessPartnerId = null;
    if (result && result.BusinessPartner) {
      businessPartnerId = result.BusinessPartner;
    }

    return {
      success: true,
      message: 'Business Partner created successfully in SAP',
      businessPartnerId: businessPartnerId,
      data: result
    };

  } catch (error) {
    console.error('SAP Business Partner integration error:', error);
    throw error;
  }
};

/**
 * Builds the SAP Business Partner payload from customer data
 * @param {Object} customerData - Customer form data
 * @param {string} sfdcAccountId - Salesforce Account ID
 * @returns {Object} Formatted payload for SAP BP API
 */
const buildSapBusinessPartnerPayload = (customerData, sfdcAccountId) => {
  // Create business partner name (FirstLastService)
  const businessPartnerName = `${customerData.firstName}${customerData.lastName}${customerData.serviceType === 'commercial' ? 'Commercial' : 'Residence'}`;
  
  // Parse street address to extract house number and street name
  const addressParts = parseStreetAddress(customerData.streetAddress);
  
  const cityName = customerData.city || '';
  const country = 'US'; // Default to US
  
  return {
    "Customer": "1",
    "Supplier": "",
    "AcademicTitle": "",
    "AuthorizationGroup": "",
    "BusinessPartnerCategory": "1",
    "BusinessPartnerName": businessPartnerName,
    "FirstName": customerData.firstName,
    "LastName": customerData.lastName,
    "BusinessPartnerIDByExtSystem": sfdcAccountId || "",
    "to_BusinessPartnerAddress": [
      {
        "HouseNumber": addressParts.houseNumber,
        "StreetName": addressParts.streetName,
        "PostalCode": customerData.zipCode,
        "CityName": cityName,
        "Country": country,
        "to_AddressUsage": [
          {
            "AddressUsage": "XXDEFAULT"
          }
        ]
      }
    ]
  };
};

/**
 * Parses street address to extract house number and street name
 * @param {string} streetAddress - Full street address
 * @returns {Object} Object with houseNumber and streetName
 */
const parseStreetAddress = (streetAddress) => {
  if (!streetAddress) {
    return { houseNumber: '', streetName: '' };
  }

  // Try to match common patterns like "123 Main Street" or "123A Oak Ave"
  const match = streetAddress.match(/^(\d+[A-Za-z]?)\s+(.+)$/);
  
  if (match) {
    return {
      houseNumber: match[1],
      streetName: match[2]
    };
  }
  
  // If no house number found, return the whole address as street name
  return {
    houseNumber: '',
    streetName: streetAddress
  };
};

/**
 * Check if SAP Business Partner integration is available
 * @returns {boolean} True if SAP BP API is configured and available
 */
export const isSapAvailable = () => {
  return !!(API_CONFIG.SAP_BP_API_URL && API_CONFIG.SAP_BP_API_URL.trim() !== '');
};
